import { useState } from "react";
import Grid from "@mui/material/Grid";
import Alert from "@mui/material/Alert";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";

import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import PageGuide from "components/admin/PageGuide";
import Footer from "examples/Footer";
import PageCard from "components/admin/PageCard";
import AdminTable from "components/admin/AdminTable";
import { useConfirm } from "components/admin/Feedback";
import { useFetch, useRunner } from "components/admin/hooks";
import api from "api/client";
import { useAuth } from "auth/AuthContext";
import { formatDate } from "utils/format";
import {
  EMPTY_ACCOUNT,
  NewAccountFields,
  CreatedAccountAlert,
  createAccount,
  isNewAccountValid,
} from "layouts/players/CreateAccount";

const EMPTY_FORM = { username: "", permissions: [], note: "" };

/** Quyền kèm mọi quyền bắt buộc của nó (sửa boss -> xem boss). */
function withRequired(catalog, keys) {
  const result = new Set(keys);
  let added = true;
  while (added) {
    added = false;
    catalog.forEach((p) => {
      if (result.has(p.key) && p.requires && !result.has(p.requires)) {
        result.add(p.requires);
        added = true;
      }
    });
  }
  return catalog.map((p) => p.key).filter((key) => result.has(key));
}

/** Bỏ quyền cùng mọi quyền phụ thuộc vào nó (bỏ xem boss -> bỏ luôn sửa boss, sửa quà boss). */
function withoutDependents(catalog, keys, removed) {
  const result = new Set(keys);
  result.delete(removed);
  let changed = true;
  while (changed) {
    changed = false;
    catalog.forEach((p) => {
      if (result.has(p.key) && p.requires && !result.has(p.requires)) {
        result.delete(p.key);
        changed = true;
      }
    });
  }
  return catalog.map((p) => p.key).filter((key) => result.has(key));
}

function Permissions() {
  const confirm = useConfirm();
  const { admin } = useAuth();
  const [run, busy] = useRunner();
  const [form, setForm] = useState(EMPTY_FORM);
  const [isNew, setIsNew] = useState(false);
  const [account, setAccount] = useState(EMPTY_ACCOUNT);
  const [created, setCreated] = useState(null);
  const list = useFetch(() => api.get("/api/admin/permissions"), []);
  const catalog = list.data?.catalog || [];
  const groups = [...new Set(catalog.map((p) => p.group))];
  const labelOf = (key) => catalog.find((p) => p.key === key)?.label || key;

  const toggle = (key) =>
    setForm({
      ...form,
      permissions: form.permissions.includes(key)
        ? withoutDependents(catalog, form.permissions, key)
        : withRequired(catalog, [...form.permissions, key]),
    });

  const save = async () => {
    if (!isNew) {
      const row = (list.data?.items || []).find((r) => r.username === form.username);
      const before = row?.permissions || [];
      const added = form.permissions.filter((k) => !before.includes(k));
      const removed = before.filter((k) => !form.permissions.includes(k));
      // Chỉ đổi ghi chú thì lưu luôn, không cần hỏi.
      if (added.length > 0 || removed.length > 0) {
        const self = row?.userId === admin?.userId;
        const yes = await confirm({
          title: `Lưu quyền cho ${form.username || "tài khoản này"}`,
          message: [
            added.length ? `Thêm ${added.length} quyền:` : null,
            ...added.map((k) => `  + ${labelOf(k)}`),
            removed.length ? `Bỏ ${removed.length} quyền:` : null,
            ...removed.map((k) => `  − ${labelOf(k)}`),
            "",
            self && removed.length
              ? "⚠ Đây là tài khoản của BẠN — bỏ quyền thì bạn mất quyền đó ngay."
              : null,
            "Máy chủ áp dụng ngay; menu của người đó đổi khi họ tải lại trang.",
          ]
            .filter((l) => l !== null)
            .join("\n"),
          color: removed.length && self ? "error" : "warning",
          confirmText: "Lưu quyền",
        });
        if (!yes) return;
      }
    }
    const res = isNew
      ? await run(
          () => createAccount({ ...form, username: form.username.trim(), ...account }),
          (data) => `Đã tạo tài khoản ${data.username}`
        )
      : await run(
          () => api.put("/api/admin/permissions", form),
          (data) => `Đã lưu quyền cho ${data.username}`
        );
    if (res.ok) {
      if (isNew) setCreated(res.result);
      setForm(EMPTY_FORM);
      setAccount(EMPTY_ACCOUNT);
      setIsNew(false);
      list.reload(true);
    }
  };

  const remove = async (row) => {
    if (
      !(await confirm({
        title: "Gỡ quyền",
        message: `Gỡ toàn bộ quyền web admin của ${row.username}? Tài khoản sẽ bị đăng xuất trong tối đa 1 phút.`,
        color: "error",
        confirmText: "Gỡ quyền",
      }))
    )
      return;
    const res = await run(() => api.del(`/api/admin/permissions/${row.userId}`), "Đã gỡ quyền");
    if (res.ok) list.reload(true);
  };

  /** Mất / đổi điện thoại: gỡ 2 lớp, lần đăng nhập sau quét QR mới. */
  const resetTotp = async (userId, username) => {
    const self = userId === admin?.userId;
    if (
      !(await confirm({
        title: "Gỡ xác thực 2 lớp",
        message: self
          ? "Gỡ xác thực 2 lớp của chính bạn? Phiên này chỉ còn dùng được từ IP trong danh sách được phép. Lần đăng nhập sau bạn quét QR mới trên điện thoại mới (từ IP được phép nếu danh sách IP khác rỗng)."
          : `Gỡ xác thực 2 lớp của ${username}? Lần đăng nhập sau họ phải quét QR mới, và phải đăng nhập từ IP được phép nếu danh sách IP khác rỗng.`,
        color: "error",
        confirmText: "Gỡ 2 lớp",
      }))
    )
      return;
    const res = await run(
      () => api.del(`/api/admin/permissions/${userId}/totp`),
      "Đã gỡ xác thực 2 lớp"
    );
    if (res.ok) list.reload(true);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <PageGuide page="permissions" />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <PageCard title="Cấp quyền" subtitle="Cho tài khoản web khác vào một phần web admin">
              <Alert severity="info" sx={{ mb: 2 }}>
                Người được cấp quyền chỉ thấy và gọi được đúng phần được tick, không vào được trang
                Phân quyền và không thao tác được lên tài khoản admin khác. Không bật isAdmin cho
                nhân vật của họ, isAdmin là quyền chủ toàn phần (cả GM trong game). Quyền màu đỏ tạo
                / xoá được tài sản hoặc ảnh hưởng cả máy chủ.
              </Alert>
              {created && (
                <CreatedAccountAlert account={created} onClose={() => setCreated(null)} />
              )}
              <MDBox
                component="form"
                display="flex"
                flexDirection="column"
                gap={2}
                onSubmit={(e) => {
                  e.preventDefault();
                  save();
                }}
              >
                <FormControlLabel
                  sx={{ ml: 0 }}
                  control={<Switch checked={isNew} onChange={(e) => setIsNew(e.target.checked)} />}
                  label={
                    <MDTypography variant="button" fontWeight="medium">
                      {isNew
                        ? "Tạo tài khoản mới rồi cấp quyền (đăng nhập được cả game lẫn web admin)"
                        : "Cấp quyền cho tài khoản đã có (bật để tạo tài khoản mới)"}
                    </MDTypography>
                  }
                />
                <Grid container spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      fullWidth
                      label={
                        isNew
                          ? "Tên đăng nhập mới (tối đa 20 ký tự)"
                          : "Tài khoản web (tên đăng nhập)"
                      }
                      value={form.username}
                      inputProps={{ maxLength: 20 }}
                      autoComplete="off"
                      onChange={(e) => setForm({ ...form, username: e.target.value })}
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDInput
                      fullWidth
                      label="Ghi chú"
                      value={form.note}
                      onChange={(e) => setForm({ ...form, note: e.target.value })}
                    />
                  </Grid>
                </Grid>
                {isNew && <NewAccountFields value={account} onChange={setAccount} />}
                <MDBox display="flex" gap={1} alignItems="center" flexWrap="wrap">
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() => setForm({ ...form, permissions: catalog.map((p) => p.key) })}
                  >
                    Chọn tất cả
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="info"
                    onClick={() =>
                      setForm({
                        ...form,
                        permissions: withRequired(
                          catalog,
                          catalog.filter((p) => !p.requires).map((p) => p.key)
                        ),
                      })
                    }
                  >
                    Chỉ quyền xem
                  </MDButton>
                  <MDButton
                    size="small"
                    variant="outlined"
                    color="secondary"
                    onClick={() => setForm({ ...form, permissions: [] })}
                  >
                    Bỏ chọn hết
                  </MDButton>
                  <MDTypography variant="caption" color="text" ml={1}>
                    Đã chọn {form.permissions.length}/{catalog.length} quyền
                  </MDTypography>
                </MDBox>
                <Grid container spacing={2}>
                  {groups.map((group) => (
                    <Grid item xs={12} sm={6} lg={4} key={group}>
                      <MDBox
                        p={1.5}
                        height="100%"
                        borderRadius="lg"
                        sx={({ palette: { grey } }) => ({ border: `1px solid ${grey[300]}` })}
                      >
                        <MDTypography variant="button" fontWeight="bold" display="block" mb={0.5}>
                          {group}
                        </MDTypography>
                        {catalog
                          .filter((p) => p.group === group)
                          .map((p) => (
                            <MDBox key={p.key} mb={0.5}>
                              <FormControlLabel
                                sx={{ mr: 0 }}
                                control={
                                  <Checkbox
                                    size="small"
                                    checked={form.permissions.includes(p.key)}
                                    onChange={() => toggle(p.key)}
                                  />
                                }
                                label={
                                  <MDTypography
                                    variant="button"
                                    fontWeight="medium"
                                    color={p.danger ? "error" : "dark"}
                                  >
                                    {p.label}
                                  </MDTypography>
                                }
                              />
                              <MDTypography variant="caption" color="text" display="block" ml={4}>
                                {p.description}
                              </MDTypography>
                            </MDBox>
                          ))}
                      </MDBox>
                    </Grid>
                  ))}
                </Grid>
                <MDBox>
                  <MDButton
                    type="submit"
                    variant="gradient"
                    color="info"
                    disabled={
                      busy ||
                      !form.username.trim() ||
                      form.permissions.length === 0 ||
                      (isNew && !isNewAccountValid(account))
                    }
                  >
                    <Icon>{isNew ? "person_add" : "save"}</Icon>&nbsp;
                    {isNew ? "Tạo tài khoản và cấp quyền" : "Lưu quyền"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </PageCard>
          </Grid>
          <Grid item xs={12}>
            <PageCard
              title="Tài khoản được phân quyền"
              subtitle="Đổi / gỡ quyền có hiệu lực trong tối đa 1 phút"
              color="warning"
            >
              <MDBox mx={-2}>
                {list.error && <Alert severity="error">{list.error}</Alert>}
                {list.data && (
                  <MDBox px={2} mb={2}>
                    <Alert
                      severity={list.data.selfTotp ? "success" : "warning"}
                      action={
                        list.data.selfTotp && (
                          <MDButton
                            size="small"
                            variant="text"
                            color="error"
                            disabled={busy}
                            onClick={() => resetTotp(admin.userId, admin.username)}
                          >
                            Gỡ (đổi điện thoại)
                          </MDButton>
                        )
                      }
                    >
                      {list.data.selfTotp
                        ? "Tài khoản của bạn đã bật xác thực 2 lớp: vào được web admin từ mọi IP bằng mã trên điện thoại."
                        : "Tài khoản của bạn chưa bật xác thực 2 lớp. Đăng xuất rồi đăng nhập lại để quét QR."}
                      {!list.data.requireTotp &&
                        " Máy chủ đang tắt bắt buộc 2 lớp (adminRequireTotp = false trong server.json)."}
                    </Alert>
                  </MDBox>
                )}
                <AdminTable
                  loading={list.loading}
                  rows={list.data?.items || []}
                  rowKey="userId"
                  emptyText="Chưa cấp quyền cho ai"
                  columns={[
                    {
                      key: "username",
                      label: "Tài khoản",
                      render: (r) => (
                        <>
                          {r.username}
                          {r.characterName && (
                            <MDTypography variant="caption" color="text" display="block">
                              {r.characterName}
                            </MDTypography>
                          )}
                        </>
                      ),
                    },
                    {
                      key: "permissions",
                      label: "Quyền",
                      wrap: true,
                      render: (r) => (
                        <MDBox display="flex" flexWrap="wrap" gap={0.5}>
                          {r.permissions.map((key) => (
                            <Chip
                              key={key}
                              size="small"
                              label={labelOf(key)}
                              color={
                                catalog.find((p) => p.key === key)?.danger ? "error" : "default"
                              }
                            />
                          ))}
                        </MDBox>
                      ),
                    },
                    {
                      key: "totp",
                      label: "2 lớp",
                      render: (r) => (
                        <Chip
                          size="small"
                          label={r.totp ? "Đã bật" : "Chưa bật"}
                          color={r.totp ? "success" : "default"}
                        />
                      ),
                    },
                    { key: "note", label: "Ghi chú", wrap: true },
                    {
                      key: "updateTime",
                      label: "Cập nhật",
                      render: (r) => (
                        <>
                          {formatDate(r.updateTime)}
                          <MDTypography variant="caption" color="text" display="block">
                            bởi {r.grantedBy}
                          </MDTypography>
                        </>
                      ),
                    },
                    {
                      key: "actions",
                      label: "",
                      align: "right",
                      render: (r) => (
                        <>
                          <MDButton
                            size="small"
                            variant="text"
                            color="info"
                            disabled={busy}
                            onClick={() => {
                              setIsNew(false);
                              setForm({
                                username: r.username,
                                permissions: r.permissions,
                                note: r.note || "",
                              });
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            Sửa
                          </MDButton>
                          {r.totp && (
                            <MDButton
                              size="small"
                              variant="text"
                              color="warning"
                              disabled={busy}
                              onClick={() => resetTotp(r.userId, r.username)}
                            >
                              Gỡ 2 lớp
                            </MDButton>
                          )}
                          <MDButton
                            size="small"
                            variant="text"
                            color="error"
                            disabled={busy}
                            onClick={() => remove(r)}
                          >
                            Gỡ quyền
                          </MDButton>
                        </>
                      ),
                    },
                  ]}
                />
              </MDBox>
            </PageCard>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default Permissions;
